import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/** Deletes a single editable treatment and invalidates relevant query caches. */
export function useDeleteTreatment(options?: { patientId?: string; consultationId?: string }) {
  const api = useOsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete('/treatments/:id', { params: { id } })
      if (!response.success) throw new Error(response.error.message)
      return response.data.id
    },
    onSuccess: () => {
      toast.success('Treatment removed')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['product'] })
      if (options?.patientId) {
        queryClient.invalidateQueries({ queryKey: ['patient-orders', options.patientId] })
        queryClient.invalidateQueries({ queryKey: ['patient-treatments', options.patientId] })
        queryClient.invalidateQueries({ queryKey: ['patient-timeline', options.patientId] })
      }
      if (options?.consultationId) {
        queryClient.invalidateQueries({ queryKey: ['consultation', options.consultationId] })
      }
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      if (options?.patientId) {
        queryClient.invalidateQueries({ queryKey: ['patient-tasks', options.patientId] })
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to remove treatment')
    },
  })
}
